<template>
  <div :class="['chat-container', {'full': fullScreenMode }]">
    <ChatHeader
      :is-connected="isConnected"
      :full-screen-mode="fullScreenMode"
      :gpt-version="gptVersion"
      :question-count="questionCount"
      @expand="handleExpand"
      @close="handleClose"
    />
    <div class="chat-body">
      <div class="chat-body-content" :class="{ 'side-tab-open': showSideTab }">
        <div class="message-list-wrapper">
          <MessageList
            :messages="messages"
            :is-loading="isLoading"
            :is-answering="isAnswering"
            :full-screen-mode="fullScreenMode"
            @submit="handleSubmitQuestion"
            @stop="stopAnswering"
          />
          <div class="button-wrapper">
            <button v-if="!showSideTab" class="guide-button" @click="showSideTab = true">
              <span>{{ $t('mine.chat.questionGuide') }}</span>
            </button>
            <button v-if="messages.length" class="reset-button" @click="resetHistoryMessages">
              <span>{{ $t('mine.chat.reset') }}</span>
              <i class="icon icon--reset-red"></i>
            </button>
          </div>
          <button v-if="isStopAvailable" class="pause" @click="stopAnswering">
            <i class="icon icon--pause-answering" />
            <p>{{ $t('mine.chat.pause') }}</p>
          </button>
        </div>
        <Transition>
          <ChatSideBar
            v-if="showSideTab"
            :disabled="isAskDisabled"
            @close="showSideTab = false"
            @submit="handleSubmitQuestion"
          />
        </Transition>
      </div>
      <AskContainer
        :is-loading="isLoading"
        :is-answering="isAnswering"
        :limit-reached="tokenLimitReached"
        :disabled="isAskDisabled"
        @submit="handleSubmitQuestion"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { upperFirst } from '@/libs/utils'
import { askQuestion, abortRendering, connect, setReportForChat, setSummary, getTokenStatus, resetReportChat, docentCheck, docentStop } from '@/api/ai'
import ChatHeader from './ChatHeader.vue'
import MessageList from './MessageList.vue'
import AskContainer from './AskContainer.vue'
import ChatSideBar from './ChatSideBar.vue'
import { FREQUENCY_PV_RANGE, FREQUENCY_SESSION_RANGE } from '@/libs/constants'
export default {
  name: 'ChatContainer',
  components: {
    ChatHeader,
    MessageList,
    AskContainer,
    ChatSideBar
  },
  props: {
    reportList: {
      type: Array,
      required: true
    }
  },
  data () {
    return {
      category: 'reportchat',
      messages: [],
      isConnected: false,
      isLoading: false,
      isAnswering: false,
      gptVersion: '',
      userInfo: null,
      fullScreenMode: false,
      showSideTab: true,
      tokenLimitReached: false,
      questionCount: {
        total: 0,
        count: 0,
        exceeded: false
      }
    }
  },
  computed: {
    ...mapState([
      'userData',
      'domain',
      'isDemo'
    ]),
    isAskDisabled () {
      return this.isLoading || this.isAnswering || this.tokenLimitReached || this.questionCount.exceeded
    },
    isStopAvailable () {
      return (this.isLoading || this.isAnswering) && this.messages[0].type !== 'summary'
    },
    chatUserInfo () {
      return {
        email: this.isDemo ? 'demo' : this.userData.userId,
        organization: this.userData.userName,
        userNum: this.userData.userNum,
        lang: this.$i18n.locale
        // parentSid: this.domain.parentSid,
        // domain: this.domain.domain
      }
    }
  },
  async created () {
    this.checkQuestionLimit()
    await connect(this.setConnection)
    setReportForChat({
      serviceType: 'beusable',
      category: this.category,
      _ids: this.reportList.map(report => report.id)
    }).then(() => {
      this.setSummaryReport()
    }).catch((e) => {
      this.insertMessage({
        role: 'ai',
        type: 'summary',
        error: 'default'
      })
      console.log('setReportForChat error', e)
    })
  },
  mounted () {
  },
  beforeDestroy () {
    sessionStorage.removeItem('reportchat_accessToken')
    sessionStorage.removeItem('reportchat_refreshToken')
  },
  methods: {
    setConnection (version) {
      this.isConnected = true
      if (version) this.gptVersion = version
    },

    handleSubmitQuestion (question) {
      if (this.isAskDisabled) return

      this.insertMessage({ role: 'human', message: question.prompt })
      this.insertMessage({
        role: 'ai',
        message: ''
      })
      this.isLoading = true

      const questionData = {
        prompt: question.prompt,
        category: this.category,
        info: this.chatUserInfo,
        serviceType: 'beusable'
      }

      askQuestion(questionData, this.startAnswering, this.onAnswering, this.endAnswering)
    },

    insertMessage (message) {
      this.messages.unshift(message)
    },

    startAnswering (res) {
      this.checkQuestionLimit()
      this.messages[0].id = res.headers.get('id')

      if (res.headers.get('Askcode') === '599') {
        this.setError()
      } else {
        this.isAnswering = true
      }

      this.messages[0].message = ''
      this.isLoading = false
    },

    onAnswering (chunk) {
      this.messages[0].message += chunk
    },

    endAnswering (res, error) {
      if (res) {
        const id = res.headers.get('id')
        setTimeout(() => {
          this.getTokenStatus(id)
        }, 2000)
      }
      if (error) {
        this.setError(error.name === 'AbortError' ? 'aborted' : 'default')
      }
      this.isLoading = false
      this.isAnswering = false
    },
    setError (type = 'default') {
      this.messages[0].error = type
    },
    stopAnswering () {
      const params = {
        'category': 'reportchat',
        'serviceType': 'beusable'
      }
      abortRendering()
      docentStop(params)
        .then((res) => {
          const result = res.result
          this.questionCount.count = result.count
          this.questionCount.exceeded = !result.state
        })
        .catch((e) => {
          console.log(e)
        })
      this.isAnswering = false
    },
    parseFreqArr (arr) {
      return arr.flatMap(item => {
        item = item.toString()
        const nums = item.replace(/[\[\]]/g, '')

        if (nums.includes('-')) {
          const [start, end] = nums.split('-').map(Number)
          return [start, end]
        }

        if (nums.includes('+')) {
          return -1
        }

        return [Number(nums)]
      })
    },
    formatFreqRange (arr, type) {
      if (!Array.isArray(arr) || arr.length === 0) {
        return String(this.$t('common.all'))
      }

      const freqArr = this.parseFreqArr(arr)

      if (freqArr.includes(-1) && freqArr.includes(1)) {
        return String(this.$t('common.all'))
      }

      const RANGE_MAP = type === 'session'
        ? FREQUENCY_SESSION_RANGE
        : FREQUENCY_PV_RANGE

      const DEFAULT_MAX = Math.max(...RANGE_MAP[1]) + 1

      const newNumber = freqArr.map(item => item === -1 ? DEFAULT_MAX : item)
      const min = Math.min(...newNumber)
      const max = Math.max(...newNumber)

      const ranges = Object.values(RANGE_MAP)
        .filter(value => {
          if (typeof value === 'number') {
            if (value === -1 && max === DEFAULT_MAX) {
              return value
            }
            return value >= min && value <= max
          }
          const [start, end] = value
          return start <= max && end >= min
        })
        .map(value => {
          if (typeof value === 'number') {
            if (value === -1) {
              return {
                start: type === 'session' ? 31 : 51,
                end: type === 'session' ? 31 : 51,
                text: type === 'session' ? `[${'31+'}]` : `[${'51+'}]`
              }
            }
            return { start: value, end: value, text: `[${value}]` }
          }
          const [start, end] = value
          return { start, end, text: `[${start}-${end}]` }
        })
        .sort((a, b) => a.start - b.start)

      return `[${ranges.map(r => r.text).join(',')}]`
    },
    async setSummaryReport () {
      this.isAnswering = true
      this.insertMessage({
        role: 'ai',
        type: 'summary',
        message: ''
      })

      const reportDataList = []
      const reportRefList = []

      this.reportList.forEach((reportData) => {
        reportDataList.push(reportData)
        reportRefList.push(reportData)
      })

      const parsedStartDates = reportDataList.map(report => new Date(report.startDate))
      const parsedEndDates = reportDataList.map(report => new Date(report.endDate))

      const minDate = new Date(Math.min(...parsedStartDates)).toISOString().slice(0, 10)
      const maxDate = new Date(Math.max(...parsedEndDates)).toISOString().slice(0, 10)
      const reportType = [...new Set(reportDataList.map(report => report.type ? upperFirst(report.type) : 'Scroll'))].join(', ')
      const url = [...new Set(reportDataList.map(report => report.matchType ? `[${report.matchType}] ${report.url}` : report.url))].join(', ')
      const refer = [...new Set(reportDataList.map(report => report.refer && report.refer !== 'all' ? report.refer : this.$t('common.all')))].join(', ')
      const visitType = [...new Set(reportDataList.map(report => report.existing).map(type => type ? this.$t('common.return') : type === false ? this.$t('common.new') : this.$t('common.all')))].join(', ')
      const hour = [...new Set(reportDataList.map(report => report.hour ? `${report.hour} : 00 ~ ${report.hour} : 59` : this.$t('common.all')))].join(', ')
      const freq = [
        ...new Set(
          reportDataList.map(report =>
            this.formatFreqRange(report.freq, report.freq_option)
          )
        )
      ].join(', ')
      const ctaDom = [...new Set(reportDataList.map(report => report.ctaDom ? report.ctaDom : this.$t('common.all')))].join(', ')

      let device = ''
      if (reportDataList.some(report => !report.device)) {
        device = [this.$t('common.deviceType.desktop'), this.$t('common.deviceType.tablet'), this.$t('common.deviceType.phone')].join(', ')
      } else {
        device = [...new Set(reportDataList.map(report => upperFirst(report.device)))].join(', ')
      }

      let hasUtm = false
      let marketingStr = ''

      reportRefList.forEach((report, i) => {
        const marketing = report.utm
        if (!marketing) return
        if (marketing) hasUtm = true

        const marketingData = {}
        marketingData.data = marketing
        marketingStr += `\n  - Data ${i + 1} - UTM`

        if (marketing) {
          for (const key in marketingData.data) {
            const val = marketingData.data[key]
            if (val && val.length) {
              const formatted = val
                .map(value =>
                  value === '__null__'
                    ? 'Inflow without utm_source parameter'
                    : value
                )
                .join(', ')

              marketingStr += `\n    - ${key}: ${formatted}`
            }
          }
        }
      })
      const marketingTitle = hasUtm ? 'UTM' : this.$t('mine.chat.summary.noMarketing')

      const summary = `${this.$t('mine.chat.summary.header', { reportCount: this.reportList.length })}

- ${this.$t('mine.chat.summary.included')}: ${reportDataList.length}${this.$t('mine.chat.summary.count')}
- ${this.$t('mine.chat.summary.type')}: ${reportType}
- ${this.$t('mine.report.detail.url')}: ${url}
- ${this.$t('mine.report.detail.period')}: ${minDate} ~ ${maxDate}
- ${this.$t('mine.report.detail.utm')}: ${marketingTitle}\n${marketingStr}
- ${this.$t('mine.report.detail.device')}: ${device}
- ${this.$t('mine.report.detail.refer')}: ${refer}
- ${this.$t('mine.report.detail.existing')}: ${visitType}
- ${this.$t('mine.report.detail.hour')}: ${hour}
- ${this.$t('mine.report.detail.freq')}: ${freq}
- ${this.$t('mine.report.detail.ctaDom')}: ${ctaDom}

${this.$t('mine.chat.summary.footer')}`

      setSummary({
        category: this.category,
        _ids: this.reportList.map(report => report.id),
        message: summary,
        info: this.chatUserInfo,
        serviceType: 'beusable'
      }).catch((e) => {
        console.log('setSummary error', e)
      })

      for (let i = 0; i < summary.length; i++) {
        await this.sleep(15)
        this.messages[0].message += summary[i]
      }
      this.isAnswering = false
    },
    sleep (ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },
    getTokenStatus (id) {
      getTokenStatus(id)
        .then((res) => {
          if (res.data.data.alert) {
            this.tokenLimitReached = true
            this.insertMessage({
              role: 'ai',
              error: 'limit'
            })
          }
        })
        .catch((e) => {
          console.log(e)
        })
    },
    async resetHistoryMessages () {
      await resetReportChat()
      this.messages = []
      this.tokenLimitReached = false
    },
    checkQuestionLimit () {
      const params = {
        'category': 'reportchat',
        'serviceType': 'beusable'
      }
      docentCheck(params)
        .then((res) => {
          const result = res.result
          this.questionCount.total = result.total
          this.questionCount.count = result.count
          this.questionCount.exceeded = !result.state
        })
        .catch((e) => {
          console.log(e)
        })
    },
    handleClose () {
      this.$emit('close')
    },
    handleExpand () {
      this.fullScreenMode = !this.fullScreenMode
    }
  }
}
</script>

<style lang="scss" scoped>
@mixin full-style {
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  border: none;
  border-radius: 0;
  box-shadow: none;
}

.chat-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 1168px;
  height: 714px;
  max-width: calc(100% - 100px);
  max-height: calc(100% - 100px);

  padding: 16px;
  border-radius: 16px;
  border: 1px solid rgba(26, 26, 26, 0.24);
  background-color: #FFF;
  box-shadow: 0px 16px 20px 0px rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
  color: #3d3d3d;
  line-height: 1;

  &.full {
    @include full-style;
  }

  @media (max-width: 768px) {
    @include full-style;
  }

  .chat-body {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: calc(100% - 59px);
    width: 100%;
    border: 1px solid #C3C3C3;
    border-radius: 6px;
    background-color: #ECECEC;
    box-shadow: 0px 3px 6px 0px #0000001F;
    box-sizing: border-box;
    overflow: hidden;

    &-content {
      display: flex;
      height: calc(100% - 82px);

      .message-list-wrapper {
        position: relative;
        flex: 1 0 100%;
        transition: all 0.3s ease-in-out;
        .button-wrapper {
          position: absolute;
          top: 20px;
          right: 25px;
          display: flex;
          gap: 10px;
          button {
            @include flexYcenter;
            gap: 8px;
            padding: 11px 12px;
            border-radius: 5px;
            font-size: 14px;
            font-weight: 600;
          }
          .guide-button {
            background-color: #EC0047;
            color: #fff;
          }
          .reset-button {
            background-color: #fff;
            box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.25);
            color: $brandColor;
          }
        }

        .pause {
          @include flexYcenter;
          gap: 4px;
          position: absolute;
          bottom: 10px;
          right: 25px;
          color: #7D7D7D;
          font-size: 14px;
          font-weight: 600;
        }
      }

      &.side-tab-open {
        .message-list-wrapper {
          flex-basis: calc(100% - 384px);
        }

        /deep/ .side-bar {
          flex-basis: 384px;
        }
        /deep/ .message-item {
          padding: 20px 40px;
        }
      }
    }
  }
}
</style>
